import clsx from 'clsx';
import { type CustomContentProps, useSnackbar } from 'notistack';
import { forwardRef } from 'react';

import { CloseV4Icon } from '../../../../icons/CloseV4Icon';
import { Flex } from '../../../common/Flex';
import { Text } from '../../../common/Text';

import styles from './Notification.module.scss';

const ICONS = {
  success: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.5 8C1.5 4.41136 4.41136 1.5 8 1.5C11.5886 1.5 14.5 4.41136 14.5 8C14.5 11.5886 11.5886 14.5 8 14.5C4.41136 14.5 1.5 11.5886 1.5 8ZM8 2.5C4.96364 2.5 2.5 4.96364 2.5 8C2.5 11.0364 4.96364 13.5 8 13.5C11.0364 13.5 13.5 11.0364 13.5 8C13.5 4.96364 11.0364 2.5 8 2.5Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.3216 5.11715C11.533 5.29476 11.5605 5.61015 11.3829 5.8216L7.18285 10.8216C7.08977 10.9324 6.95324 10.9975 6.80854 10.9999C6.66384 11.0024 6.52517 10.9421 6.42835 10.8345L4.62835 8.83448C4.44362 8.62923 4.46026 8.31308 4.66552 8.12835C4.87077 7.94362 5.18692 7.96026 5.37165 8.16552L6.78699 9.73812L10.6171 5.1784C10.7948 4.96696 11.1102 4.93954 11.3216 5.11715Z"
        fill="currentColor"
      />
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.5 8C1.5 4.41136 4.41136 1.5 8 1.5C11.5886 1.5 14.5 4.41136 14.5 8C14.5 11.5886 11.5886 14.5 8 14.5C4.41136 14.5 1.5 11.5886 1.5 8ZM8 2.5C4.96364 2.5 2.5 4.96364 2.5 8C2.5 11.0364 4.96364 13.5 8 13.5C11.0364 13.5 13.5 11.0364 13.5 8C13.5 4.96364 11.0364 2.5 8 2.5Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.99341 4.50158C8.08572 4.50071 8.17724 4.51865 8.26239 4.55432C8.34758 4.59002 8.4246 4.64271 8.48874 4.70917C8.55288 4.77564 8.6028 4.85449 8.63543 4.94089C8.66806 5.0273 8.68274 5.11946 8.67855 5.21173L8.49945 9.02347C8.48692 9.29018 8.26703 9.49999 8.00002 9.5C7.73301 9.50001 7.51311 9.29022 7.50055 9.02351L7.32114 5.21173C7.317 5.12055 7.33128 5.02948 7.36312 4.94394C7.39495 4.8584 7.4437 4.78016 7.50645 4.71388C7.5692 4.64759 7.64465 4.59463 7.72832 4.55816C7.81194 4.52171 7.90205 4.50247 7.99326 4.50159"
        fill="currentColor"
      />
      <path
        d="M8 11.4972C7.87639 11.4972 7.75555 11.4605 7.65277 11.3919C7.54999 11.3232 7.46988 11.2256 7.42258 11.1114C7.37527 10.9972 7.36289 10.8715 7.38701 10.7503C7.41112 10.629 7.47065 10.5177 7.55806 10.4302C7.64547 10.3428 7.75683 10.2833 7.87807 10.2592C7.99931 10.2351 8.12497 10.2475 8.23918 10.2948C8.35338 10.3421 8.45099 10.4222 8.51967 10.525C8.58834 10.6277 8.625 10.7486 8.625 10.8722C8.625 11.0379 8.55915 11.1969 8.44194 11.3141C8.32473 11.4313 8.16576 11.4972 8 11.4972Z"
        fill="currentColor"
      />
    </svg>
  ),
};

export const Notification = forwardRef<HTMLDivElement, CustomContentProps>((props, ref) => {
  const { className, id, variant, message } = props;

  const { closeSnackbar } = useSnackbar();

  const icon = variant === 'error' || variant === 'warning' ? ICONS.warning : ICONS.success;
  const title = variant === 'error' ? 'Error' : variant === 'warning' ? 'Warning' : 'Success';

  return (
    <Flex className={clsx(styles.root, styles[`variant_${variant}`], className)} ref={ref} direction="column" gap={2}>
      <Flex className={styles.title} gap={2} alignItems="center">
        {icon}

        <Text type="regular12">{title}</Text>

        <CloseV4Icon
          className={styles.closeIcon}
          onClick={() => {
            closeSnackbar(id);
          }}
        />
      </Flex>

      <Text type="regular12" lineCount={2} color="white">
        {message}
      </Text>
    </Flex>
  );
});

Notification.displayName = 'Notification';
